import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Current policy version - update when cookie policy changes significantly
export const COOKIE_POLICY_VERSION = '1.0.0';

// Consent expires after 12 months
const CONSENT_EXPIRY_DAYS = 365;

export interface CookieConsent {
  necessary: true; // Always true
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  policy_version: string;
  consent_id: string;
}

interface CookieConsentContextType {
  consent: CookieConsent | null;
  hasConsented: boolean;
  showBanner: boolean;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (analytics: boolean, marketing: boolean) => void;
  openSettings: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | null>(null);

const STORAGE_KEY = 'snippsnapp_cookie_consent';

function generateConsentId(): string {
  return crypto.randomUUID();
}

function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const consent: CookieConsent = JSON.parse(stored);
    
    // Check if consent has expired (12 months)
    const consentDate = new Date(consent.timestamp);
    const expiryDate = new Date(consentDate);
    expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
    
    if (new Date() > expiryDate) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    // Check if policy version changed
    if (consent.policy_version !== COOKIE_POLICY_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return consent;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function saveConsent(consent: CookieConsent): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

async function logConsentToSupabase(consent: CookieConsent): Promise<void> {
  try {
    await supabase.from('cookie_consents').insert({
      consent_id: consent.consent_id,
      policy_version: consent.policy_version,
      analytics: consent.analytics,
      marketing: consent.marketing,
      user_agent: navigator.userAgent.substring(0, 255),
    } as never);
  } catch (error) {
    // Silent fail - consent logging is not critical
    console.warn('Failed to log consent:', error);
  }
}

// Cookie cleanup helper
function cleanupNonEssentialCookies(): void {
  const cookies = document.cookie.split(';');
  const essentialPrefixes = ['snippsnapp_', 'sb-']; // Supabase cookies are essential
  
  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    const isEssential = essentialPrefixes.some(prefix => cookieName.startsWith(prefix));
    
    if (!isEssential && cookieName) {
      // Try to delete cookie with various domain configurations
      const domains = [window.location.hostname, '.' + window.location.hostname, ''];
      const paths = ['/', ''];
      
      domains.forEach(domain => {
        paths.forEach(path => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domain ? `; domain=${domain}` : ''}`;
        });
      });
    }
  });
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => getStoredConsent());
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // Show banner if no consent stored
  useEffect(() => {
    const storedConsent = getStoredConsent();
    if (!storedConsent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const createAndSaveConsent = useCallback(async (analytics: boolean, marketing: boolean) => {
    const existingConsentId = consent?.consent_id || generateConsentId();
    
    const newConsent: CookieConsent = {
      necessary: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
      policy_version: COOKIE_POLICY_VERSION,
      consent_id: existingConsentId,
    };
    
    setConsent(newConsent);
    saveConsent(newConsent);
    setShowBanner(false);
    setShowModal(false);
    
    // Log to Supabase
    await logConsentToSupabase(newConsent);
    
    // If revoking consent, clean up cookies
    if (!analytics || !marketing) {
      cleanupNonEssentialCookies();
    }
    
    // Dispatch event for script loaders to react
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: newConsent }));
  }, [consent?.consent_id]);
  
  const acceptAll = useCallback(() => {
    createAndSaveConsent(true, true);
  }, [createAndSaveConsent]);
  
  const rejectAll = useCallback(() => {
    createAndSaveConsent(false, false);
  }, [createAndSaveConsent]);
  
  const savePreferences = useCallback((analytics: boolean, marketing: boolean) => {
    createAndSaveConsent(analytics, marketing);
  }, [createAndSaveConsent]);
  
  const openSettings = useCallback(() => {
    setShowModal(true);
  }, []);
  
  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsented: consent !== null,
        showBanner,
        showModal,
        setShowModal,
        acceptAll,
        rejectAll,
        savePreferences,
        openSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
}
